import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, 
  Calendar, 
  User, 
  ExternalLink, 
  Users,
  Building2,
  Film,
  Clapperboard
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PatriarchPage() {
  const [film, setFilm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFilm = async () => {
      try {
        const response = await axios.get(`${API}/films/patriarch`);
        setFilm(response.data);
      } catch (error) {
        console.error('Error fetching film:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFilm();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-neutral-950 flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!film) {
    return (
      <div className="min-h-screen pt-20 bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl text-white mb-4">FILM NOT FOUND</h1>
        </div>
      </div>
    );
  }

  const comparableFilms = film.comparable_films || ["Stranger Things", "Black Mirror", "Event Horizon", "Arrival"];

  return (
    <div data-testid="patriarch-page" className="min-h-screen bg-neutral-950">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-end overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={film.poster_url}
            alt={film.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/90 via-neutral-950/50 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            {/* Coming Soon Badge */}
            <div className="flex items-center gap-4 mb-6">
              <span className="inline-block bg-orange-600 text-white text-xs font-bold px-4 py-2 uppercase tracking-wider animate-pulse">
                COMING {film.release_date || 'DECEMBER 2026'}
              </span>
              <span className="inline-block bg-white/10 border border-white/20 text-white text-xs font-bold px-4 py-2 uppercase tracking-wider">
                {film.genre}
              </span>
            </div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl tracking-tight uppercase leading-none text-white mb-4">
              {film.title}
            </h1>

            {/* Tagline */}
            {film.tagline && (
              <p className="text-orange-500 text-xl md:text-2xl font-medium uppercase tracking-wider mb-8">
                {film.tagline}
              </p>
            )}

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-neutral-300 mb-8">
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-orange-500" />
                <span>{film.release_date || 'December 2026'}</span>
              </div>
              <div className="flex items-center gap-2">
                <User size={18} className="text-orange-500" />
                <span>Dir. {film.director}</span>
              </div>
              <div className="flex items-center gap-2">
                <Building2 size={18} className="text-orange-500" />
                <span>{film.production_company}</span>
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center gap-3 p-4 bg-white/5 border border-white/10 mb-8">
              <Clapperboard className="text-orange-500" size={24} />
              <div>
                <span className="text-white font-medium block">Currently In Production</span>
                <span className="text-neutral-400 text-sm">Filming April 2026 in the UK</span>
              </div>
            </div>

            {/* CTA */}
            {film.official_website && (
              <a
                href={film.official_website}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="patriarch-website-btn"
                className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-500 text-white font-sans font-bold py-4 px-8 uppercase tracking-widest text-sm transition-all duration-300 hover:tracking-[0.2em]"
              >
                VISIT OFFICIAL WEBSITE
                <ExternalLink size={18} />
              </a>
            )}
          </motion.div>
        </div>
      </section>

      {/* Synopsis Section */}
      <section className="py-16 md:py-24 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Synopsis */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-3xl md:text-4xl tracking-wider text-white uppercase mb-6">
                SYNOPSIS
              </h2>
              <p className="text-neutral-300 text-lg leading-relaxed mb-6">
                {film.synopsis}
              </p>
              {film.full_synopsis && (
                <p className="text-neutral-400 leading-relaxed">
                  {film.full_synopsis}
                </p>
              )}

              {/* Comparable Films */}
              <div className="mt-10">
                <h3 className="font-display text-xl tracking-wider text-white uppercase mb-4">
                  FOR FANS OF
                </h3>
                <div className="flex flex-wrap gap-3">
                  {comparableFilms.map((title, index) => (
                    <span
                      key={index}
                      className="bg-neutral-900 border border-white/10 px-4 py-2 text-neutral-300 text-sm"
                    >
                      {title}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Cast & Crew */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              {/* Cast */}
              {film.cast && film.cast.length > 0 && (
                <div className="mb-10">
                  <h3 className="font-display text-xl tracking-wider text-white uppercase mb-4 flex items-center gap-2">
                    <Users className="text-orange-500" size={24} />
                    CAST
                  </h3>
                  <div className="space-y-3">
                    {film.cast.map((actor, index) => (
                      <div
                        key={index}
                        className="bg-neutral-900 border border-white/10 p-4"
                      >
                        <span className="text-white">{actor}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Crew */}
              {film.crew && film.crew.length > 0 && (
                <div>
                  <h3 className="font-display text-xl tracking-wider text-white uppercase mb-4 flex items-center gap-2">
                    <Film className="text-orange-500" size={24} />
                    KEY CREW
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {film.crew.map((member, index) => (
                      <div key={index} className="bg-neutral-900 border border-white/10 p-4">
                        <span className="text-orange-500 text-xs uppercase tracking-wider block mb-1">
                          {member.role}
                        </span>
                        <span className="text-white font-medium">{member.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gallery / Press Image */}
      {film.gallery && film.gallery.length > 0 && (
        <section className="py-16 md:py-24 bg-neutral-900/30">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-display text-3xl md:text-4xl tracking-wider text-white uppercase mb-4">
                FIRST LOOK
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 gap-6">
              {film.gallery.map((img, index) => (
                <motion.img
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  src={img}
                  alt={`${film.title} production still`}
                  className="w-full max-w-4xl mx-auto aspect-video object-cover"
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Production Info */}
      <section className="py-16 md:py-24 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-3xl md:text-4xl tracking-wider text-white uppercase mb-6">
                FROM THE MAKERS OF OLDER GODS
              </h2>
              <p className="text-neutral-400 text-lg leading-relaxed mb-8">
                Following the success of their award-winning Lovecraftian horror "Older Gods", Wagyu Films returns with an ambitious sci-fi thriller exploring themes of redemption, sacrifice, and the price of existence.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 bg-neutral-900/50 border border-white/10">
                  <span className="font-display text-3xl text-orange-500 block mb-2">APR 2026</span>
                  <span className="text-neutral-400 text-sm uppercase tracking-wider">Filming Begins</span>
                </div>
                <div className="p-6 bg-neutral-900/50 border border-white/10">
                  <span className="font-display text-3xl text-orange-500 block mb-2">NOV 2026</span>
                  <span className="text-neutral-400 text-sm uppercase tracking-wider">Post-Production Complete</span>
                </div>
                <div className="p-6 bg-neutral-900/50 border border-white/10">
                  <span className="font-display text-3xl text-orange-500 block mb-2">DEC 2026</span>
                  <span className="text-neutral-400 text-sm uppercase tracking-wider">Release</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-neutral-900/30">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-3xl md:text-4xl tracking-wider text-white uppercase mb-6">
              STAY UPDATED
            </h2>
            <p className="text-neutral-400 text-lg leading-relaxed mb-8">
              Visit the official Patriarch website for the latest news, behind-the-scenes content, and release announcements.
            </p>
            {film.official_website && (
              <a
                href={film.official_website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-500 text-white font-sans font-bold py-4 px-10 uppercase tracking-widest text-sm transition-all duration-300 hover:tracking-[0.2em]"
              >
                VISIT OFFICIAL WEBSITE
                <ExternalLink size={18} />
              </a>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
